/**
 * Company compliance profile — the inputs statutory applicability turns on.
 *
 * Five questions decide which compliances attach to a company:
 *   1. What is it?        — company type / listing. Already captured above from MCA.
 *   2. How big is it?     — turnover, net worth, net profit, borrowings, deposits.
 *   3. How many staff?    — headcount, ESI-eligible headcount, existing registrations.
 *   4. Where does it operate? — one row per establishment, because EPF and ESI
 *                           test employees AT A PREMISES, not company-wide.
 *   5. What does it do?   — sector, digital operations, personal data, foreign money.
 *
 * This file renders questions 2–5. Question 1 is answered by the fields already
 * on the main form.
 *
 * NOTHING HERE IS MANDATORY. A blank field must reach the rule engine as "not
 * stated" so the verdict is `cannot determine` — never `does not apply`. Forcing
 * an answer only produces a guessed one, and a guessed headcount silently
 * switches EPF on or off.
 */

import { FiChevronRight } from 'react-icons/fi';
import { Select, DateField, inputSmClass } from './FormControls';
import Button from './Button';
import Chip from './Chip';
import { computeProfileCompleteness } from '../../utils/profileCompleteness';
import { deriveProfileFromRegistry, describeDerivations } from '../../utils/registryDerivations';

// ── Reference data ──────────────────────────────────────────────────────────

// All 28 states + 8 union territories. The full list matters: labour codes,
// professional tax, shops & establishments and trade licences are state
// subjects, so a state we cannot name is a rule we cannot resolve.
export const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa',
  'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala',
  'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland',
  'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman and Nicobar Islands', 'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu', 'Delhi', 'Jammu and Kashmir',
  'Ladakh', 'Lakshadweep', 'Puducherry',
];

const ESTABLISHMENT_TYPES = [
  { value: 'registered-office', label: 'Registered office' },
  { value: 'corporate-office', label: 'Corporate office' },
  { value: 'branch', label: 'Branch' },
  { value: 'factory', label: 'Factory' },
  { value: 'warehouse', label: 'Warehouse' },
  { value: 'other', label: 'Other' },
];

const STATUTES = [
  { value: 'epf', label: 'EPF' },
  { value: 'esi', label: 'ESI' },
  { value: 'gratuity', label: 'Gratuity' },
  { value: 'posh', label: 'POSH' },
  { value: 'shops-establishments', label: 'Shops & Establishments' },
  { value: 'professional-tax', label: 'Professional Tax' },
];

const POLLUTION_CATEGORIES = [
  { value: '', label: 'Not stated' },
  { value: 'red', label: 'Red' },
  { value: 'orange', label: 'Orange' },
  { value: 'green', label: 'Green' },
  { value: 'white', label: 'White' },
];

const EXCHANGES = ['BSE', 'NSE', 'MSEI'];

// ── Small helpers ───────────────────────────────────────────────────────────

/** Any date value → "YYYY-MM-DD" for <input type="date">. */
const toIso = (raw = '') => {
  const s = String(raw || '').trim();
  if (!s || s === '-') return '';
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  return s;
};

/**
 * Rupees → a crore caption under the input. Statutory thresholds are quoted in
 * crores (₹10 cr net worth, ₹100 cr turnover, ₹500 cr for CSR) while the field
 * holds absolute rupees, so without this the order-of-magnitude error is
 * invisible until it flips a determination.
 */
const croreHint = (raw) => {
  const n = parseFloat(String(raw ?? '').replace(/,/g, ''));
  if (!Number.isFinite(n) || n === 0) return '';
  return `₹${(n / 1e7).toLocaleString('en-IN', { maximumFractionDigits: 2 })} cr`;
};

/**
 * Tri-state <-> select. The schema stores true / false / null; a select emits
 * strings. `null` (not stated) has to survive the round trip, which is why this
 * cannot collapse to a checkbox.
 */
const TRI_OPTIONS = [
  { value: '', label: 'Not stated' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];
const triToSelect = (v) => (v === true ? 'yes' : v === false ? 'no' : '');
const selectToTri = (v) => (v === 'yes' ? true : v === 'no' ? false : null);

/** Today as YYYY-MM-DD, for pre-filling a new dated row. */
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const hasValue = (r) => r && r.value !== '' && r.value !== null && r.value !== undefined;

/**
 * True when an amount carries no usable answer. A stored 0 counts as blank:
 * MCA publishes 0 where a figure is withheld, and the deadline engine already
 * reads it that way, so treating it as a genuine nil would resolve a threshold
 * on an absence. Mirrors hasAmount() in profileCompleteness.js — if one changes
 * the other must, or the ledger and the field will disagree on screen.
 */
const isBlankAmount = (v) => {
  if (v === '' || v === null || v === undefined) return true;
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/,/g, '').trim());
  return !Number.isFinite(n) || n === 0;
};

/**
 * Shown on any row that will be discarded on save for want of a required field.
 * Both `datedFigure.effectiveFrom` and `establishment.state` are required by the
 * schema, so a row missing one cannot be stored — but dropping typed input
 * without saying so is how a user comes to believe a head count was recorded
 * when it never was.
 */
function DropWarning({ children }) {
  return (
    <div className="mt-[3px] flex items-start gap-1 text-[10px] font-semibold leading-tight" style={{ color: '#8a6a1f' }}>
      <span aria-hidden="true">!</span>
      <span>{children}</span>
    </div>
  );
}

function IconTrash() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
      <path d="M2.5 4h11M6 4V2.5h4V4M4 4l.6 9.2a1 1 0 0 0 1 .8h4.8a1 1 0 0 0 1-.8L12 4"
        stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const cellCls = 'px-2 py-[5px]';
const microLabel = 'text-[9.5px] font-bold uppercase tracking-[.7px] text-[#7a7060] mb-[3px]';
const noteCls = 'text-[11px] text-[#7a7060] leading-snug';

function TriField({ label, value, onChange, note }) {
  return (
    <div>
      <div className={microLabel}>{label}</div>
      <Select size="sm" value={triToSelect(value)} onChange={(v) => onChange(selectToTri(v))} options={TRI_OPTIONS} />
      {note && <div className="mt-[3px] text-[10px] text-[#7a7060]">{note}</div>}
    </div>
  );
}

function AddRowButton({ onClick, disabled, children }) {
  return (
    <Button
      variant="ghost"
      size="sm"
      className="ml-auto group !py-1 !px-2.5 text-[11px] font-semibold hover:!bg-[#8a6a1f]/[0.08] hover:!text-[#8a6a1f]"
      onClick={onClick}
      disabled={disabled}
      title={disabled ? 'Fill in the existing empty row first' : ''}
      leftIcon={(
        <svg width="11" height="11" viewBox="0 0 12 12" fill="none" className="transition-transform duration-300 group-hover:rotate-90">
          <path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      )}
    >
      {children}
    </Button>
  );
}

function RemoveRowButton({ onClick, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center justify-center w-[26px] h-[26px] rounded-md transition-colors"
      style={{ color: '#7a7060' }}
      onMouseEnter={(e) => { e.currentTarget.style.background = '#f3ebd7'; e.currentTarget.style.color = '#8a6a1f'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#7a7060'; }}
      title={title}
    >
      <IconTrash />
    </button>
  );
}

/** Anchor id so the ledger's "Fill" link can scroll to the section it names. */
export const sectionAnchorId = (key) => `company-profile-section-${key}`;

function SectionShell({ anchorKey, title, question, count, open, onToggle, action, children }) {
  return (
    <div id={anchorKey ? sectionAnchorId(anchorKey) : undefined} className="border-t-2 border-[#ddd6c6] pt-3 mt-1 mb-3">
      <div className="flex items-center gap-2 mb-2">
        <button type="button" onClick={onToggle} className="group flex items-center gap-2 text-left">
          <FiChevronRight size={16} className={`text-[#7a7060] shrink-0 transition-transform duration-200 ${open ? 'rotate-90' : ''}`} />
          <div className="text-[18px] font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{title}</div>
          {question && <span className="text-[11px] font-normal text-[#7a7060]">{question}</span>}
          {count > 0 && <Chip bg="#e4f5f3" color="#145f5f">{count}</Chip>}
        </button>
        {action}
      </div>
      {open && children}
    </div>
  );
}

/**
 * An effective-dated figure series.
 *
 * Applicability moves prospectively — a company that reaches 20 employees in
 * October is liable from October, not from the start of the year — and CSR
 * needs the average net profit of the THREE preceding financial years. Neither
 * can be answered by a single current value, so these are stored as a series.
 */
function DatedSeries({ title, hint, rows, onChange, unit = 'money', valueHeader, addLabel, placeholder }) {
  const list = Array.isArray(rows) ? rows : [];
  // Guard on the FIGURE, not on the whole row. Now that a new row arrives with
  // today's date already in it, "is this row blank" can no longer mean "no date
  // and no value" — that test would never fire again, and the user could stack
  // up empty rows that all disappear on save.
  const canAdd = !list.some((r) => !hasValue(r));

  const update = (i, field, val) => onChange(list.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  // Pre-filled with today, NOT left blank. The schema requires effectiveFrom, so
  // a dateless row is dropped on save — and a figure typed into a row that then
  // vanishes without a word is the worst outcome available. Today's date is
  // visible in the field and editable, so it is a default the user can see and
  // correct, not a fact invented behind their back.
  const add = () => onChange([...list, { effectiveFrom: todayIso(), value: '', source: 'manual' }]);

  return (
    <div className="mb-3">
      <div className="flex items-center gap-2 mb-1">
        <div className={microLabel} style={{ marginBottom: 0 }}>{title}</div>
        {list.length > 0 && <span className="text-[10px] text-[#7a7060]">{list.length}</span>}
        <AddRowButton onClick={add} disabled={!canAdd}>{addLabel}</AddRowButton>
      </div>
      {hint && <div className="mb-1.5 text-[10px] text-[#7a7060]">{hint}</div>}
      <div className="rounded-xl overflow-hidden border" style={{ borderColor: '#ddd6c6' }}>
        <table className="w-full border-collapse" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '150px' }} />
            <col style={{ width: 'auto' }} />
            <col style={{ width: '30px' }} />
          </colgroup>
          <thead>
            <tr style={{ background: '#f8f5ef', borderBottom: '1px solid #ddd6c6' }}>
              {['As at', valueHeader, ''].map((h, i) => (
                <th key={i} className="text-left px-2 py-[7px] text-[10px] font-bold uppercase tracking-[.7px] text-[#7a7060]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-3 py-4 text-center text-[11.5px] text-[#7a7060]">
                  Not stated — no figure recorded
                </td>
              </tr>
            ) : list.map((r, i) => (
              <tr key={i} style={{ borderBottom: i < list.length - 1 ? '1px solid #e8e3d8' : 'none', background: i % 2 === 0 ? '#fff' : '#faf9f6' }}>
                <td className={cellCls}>
                  <DateField size="sm" value={toIso(r.effectiveFrom)} onChange={(v) => update(i, 'effectiveFrom', v)} placeholder="FY end" />
                  {!r.effectiveFrom && hasValue(r) && <DropWarning>Add a date or this figure is not saved</DropWarning>}
                </td>
                <td className={cellCls}>
                  <input
                    value={r.value ?? ''}
                    onChange={(e) => update(i, 'value', e.target.value)}
                    className={inputSmClass}
                    placeholder={placeholder}
                  />
                  {unit === 'money' && croreHint(r.value) && (
                    <div className="mt-[2px] text-[10px] text-[#7a7060]">{croreHint(r.value)}</div>
                  )}
                  {!hasValue(r) && <DropWarning>Enter a figure or this row is not saved</DropWarning>}
                </td>
                <td className={cellCls}>
                  <RemoveRowButton onClick={() => remove(i)} title="Remove row" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * What each unanswered question would buy, and what MCA can answer for free.
 *
 * Sits above the five sections so the trade is visible while they are being
 * filled in. It belongs beside the compliance checklist — that page does not
 * exist yet, and this is where the fields are.
 */
function ProfileLedger({ form, setField, openSection }) {
  const { unresolvedCount, items, allAnswered } = computeProfileCompleteness(form);
  const patch = deriveProfileFromRegistry(form);
  const derivations = describeDerivations(patch);

  const applyDerivations = () => {
    Object.entries(patch).forEach(([field, value]) => setField(field, value));
  };

  return (
    <div className="rounded-xl border p-3 mb-3" style={{ borderColor: '#ddd6c6', background: '#faf9f6' }}>
      <div className="flex items-baseline gap-2 mb-2">
        <div className="text-[13px] font-bold" style={{ color: allAnswered ? '#145f5f' : '#8a6a1f' }}>
          {allAnswered
            ? 'Everything these questions settle is answered'
            : `${unresolvedCount} obligation${unresolvedCount === 1 ? '' : 's'} unresolved`}
        </div>
        <div className="text-[10.5px] text-[#7a7060]">what each answer would settle — not a completion score</div>
      </div>

      <div className="space-y-1">
        {items.map((it) => (
          <div key={it.key} className="flex items-center gap-2 text-[11.5px]">
            <span className="w-[13px] shrink-0 text-center" style={{ color: it.isAnswered ? '#145f5f' : '#b3a995' }}>
              {it.isAnswered ? '✓' : '–'}
            </span>
            <span className={`w-[168px] shrink-0 font-semibold ${it.isAnswered ? 'text-[#7a7060]' : 'text-[#3a352c]'}`}>
              {it.label}
            </span>
            <span className="text-[#7a7060]">
              {it.isAnswered ? 'settled — ' : `resolves ${it.resolves} — `}
              {it.obligations.join(', ')}
            </span>
            {!it.isAnswered && (
              <button
                type="button"
                onClick={() => openSection(it.section)}
                className="ml-auto shrink-0 text-[11px] font-semibold underline underline-offset-2"
                style={{ color: '#145f5f' }}
              >
                Fill
              </button>
            )}
          </div>
        ))}
      </div>

      {derivations.length > 0 && (
        <div className="mt-2.5 pt-2.5 border-t flex items-center gap-2" style={{ borderColor: '#e8e3d8' }}>
          <div className="text-[11px] text-[#7a7060]">
            <span className="font-semibold text-[#3a352c]">Already in the registry data: </span>
            {derivations.join(' · ')}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto shrink-0 !py-1 !px-2.5 text-[11px] font-semibold hover:!bg-[#145f5f]/[0.08] hover:!text-[#145f5f]"
            onClick={applyDerivations}
          >
            Fill these in
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Sections ────────────────────────────────────────────────────────────────

export default function CompanyProfileSections({ form, setField, openSections, toggleSection }) {
  const establishments = Array.isArray(form.establishments) ? form.establishments : [];
  const coverage = Array.isArray(form.statutoryCoverage) ? form.statutoryCoverage : [];
  const gstRegs = Array.isArray(form.gstRegistrations) ? form.gstRegistrations : [];
  const exchanges = Array.isArray(form.exchanges) ? form.exchanges : [];

  const stateOptions = INDIAN_STATES;

  // Establishment picker for the coverage table — EPF and ESI registrations
  // belong to one premises, not to the company.
  const establishmentOptions = [
    { value: '', label: 'Company-wide' },
    ...establishments
      .map((e, i) => ({ value: e.label || e.city || e.state || `#${i + 1}`, label: e.label || [e.city, e.state].filter(Boolean).join(', ') || `Establishment ${i + 1}` }))
      .filter((o) => o.value),
  ];

  // ── 2. Financial position ─────────────────────────────────────────────────
  const financials = (
    <SectionShell
      anchorKey="financials"
      title="Financial position"
      question="How big is it?"
      open={openSections.financials}
      onToggle={() => toggleSection('financials')}
    >
      <div className={`${noteCls} mb-2.5`}>
        Decides small-company status, CSR, internal audit, cost audit, XBRL and CARO.
        Amounts in rupees — the crore equivalent is shown beneath each figure.
      </div>

      {/* First, because it settles more than anything else on the form: XBRL,
          CARO, internal financial controls, small-company status and MR-3. */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <div className="md:col-span-2">
          <div className={microLabel}>Turnover (₹)</div>
          <input
            value={form.turnover ?? ''}
            onChange={(e) => setField('turnover', e.target.value)}
            className={inputSmClass}
            placeholder="e.g. 1,00,00,00,000"
          />
          <div className="mt-[3px] text-[10px]" style={{ color: isBlankAmount(form.turnover) && String(form.turnover ?? '') !== '' ? '#8a6a1f' : '#7a7060' }}>
            {croreHint(form.turnover)
              /* A stored 0 is read as "not stated" — MCA publishes 0 where a
                 figure is withheld. Without saying so here, the box shows a
                 number while the ledger above calls the answer missing, and
                 the two look like they disagree. */
              || (String(form.turnover ?? '') !== '' && isBlankAmount(form.turnover)
                ? '0 is read as “not stated” — enter the real figure to settle 5 obligations'
                : 'Not published by MCA — take it from the financial statements')}
          </div>
        </div>
      </div>

      <DatedSeries
        title="Net worth"
        hint="Paid-up capital plus free reserves, less accumulated losses. One row per financial year end."
        rows={form.netWorthHistory}
        onChange={(v) => setField('netWorthHistory', v)}
        valueHeader="Net worth (₹)"
        addLabel="Add year"
        placeholder="e.g. 10,00,00,000"
      />

      <DatedSeries
        title="Net profit"
        hint="Section 135 tests the AVERAGE of the three preceding financial years, so three rows are needed before CSR can be determined."
        rows={form.netProfitHistory}
        onChange={(v) => setField('netProfitHistory', v)}
        valueHeader="Net profit (₹)"
        addLabel="Add year"
        placeholder="e.g. 5,00,00,000"
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[
          // Every placeholder is prefixed "e.g." so none can be read as a stored
          // answer. It matters most on public deposits: a bare "0" there looks
          // like a recorded nil, and nil deposits and unknown deposits resolve
          // the Section 73 test in opposite directions.
          ['borrowings', 'Borrowings (₹)', 'e.g. 50,00,00,000'],
          ['publicDeposits', 'Public deposits (₹)', 'e.g. 0 if none taken'],
          ['reservesAndSurplus', 'Reserves & surplus (₹)', 'e.g. 2,00,00,000'],
        ].map(([key, label, ph]) => (
          <div key={key}>
            <div className={microLabel}>{label}</div>
            <input
              value={form[key] ?? ''}
              onChange={(e) => setField(key, e.target.value)}
              className={inputSmClass}
              placeholder={ph}
            />
            {croreHint(form[key]) && <div className="mt-[3px] text-[10px] text-[#7a7060]">{croreHint(form[key])}</div>}
          </div>
        ))}
      </div>
      <div className="mt-1.5 text-[10px] text-[#7a7060]">
        Leave blank if unknown. A blank is not read as zero — zero deposits and unknown deposits are different answers.
      </div>
    </SectionShell>
  );

  // ── 3. Workforce ──────────────────────────────────────────────────────────
  const updateCov = (i, field, val) => setField('statutoryCoverage', coverage.map((c, idx) => (idx === i ? { ...c, [field]: val } : c)));
  const removeCov = (i) => setField('statutoryCoverage', coverage.filter((_, idx) => idx !== i));
  const addCov = () => setField('statutoryCoverage', [...coverage, { statute: 'epf', coveredFrom: '', coveredTo: '', registrationNo: '', establishmentRef: '', basis: '' }]);

  const workforce = (
    <SectionShell
      anchorKey="workforce"
      title="Workforce"
      question="How many staff?"
      count={coverage.length}
      open={openSections.workforce}
      onToggle={() => toggleSection('workforce')}
    >
      <div className={`${noteCls} mb-2.5`}>
        Decides EPF (20), ESI (10 or 20 by state), Gratuity (10) and POSH (10).
        Company-wide totals go here; the per-premises split goes in Establishments below,
        because EPF and ESI test employees at an establishment, not across the company.
      </div>

      <DatedSeries
        title="Total employees"
        hint="One row each time the headcount crosses a threshold. Coverage attaches from that date forward, not from the start of the year."
        rows={form.employeeCountHistory}
        onChange={(v) => setField('employeeCountHistory', v)}
        unit="count"
        valueHeader="Head count"
        addLabel="Add reading"
        placeholder="e.g. 22"
      />

      <DatedSeries
        title="ESI-eligible employees"
        hint="Only those drawing wages up to the ESI wage ceiling. Usually fewer than the total."
        rows={form.esiEligibleCountHistory}
        onChange={(v) => setField('esiEligibleCountHistory', v)}
        unit="count"
        valueHeader="Head count"
        addLabel="Add reading"
        placeholder="e.g. 14"
      />

      <div className="flex items-center gap-2 mb-1">
        <div className={microLabel} style={{ marginBottom: 0 }}>Existing registrations</div>
        <AddRowButton onClick={addCov}>Add registration</AddRowButton>
      </div>
      <div className="mb-1.5 text-[10px] text-[#7a7060]">
        Record coverage that already exists rather than inferring it. Once an establishment is
        covered, coverage does not necessarily lapse when the head count falls back below the
        threshold — leave "Covered to" blank while it is still running.
      </div>
      <div className="rounded-xl overflow-hidden border" style={{ borderColor: '#ddd6c6' }}>
        <table className="w-full border-collapse" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '150px' }} />
            <col style={{ width: '120px' }} />
            <col style={{ width: '120px' }} />
            <col style={{ width: 'auto' }} />
            <col style={{ width: '150px' }} />
            <col style={{ width: '30px' }} />
          </colgroup>
          <thead>
            <tr style={{ background: '#f8f5ef', borderBottom: '1px solid #ddd6c6' }}>
              {['Statute', 'Covered from', 'Covered to', 'Registration no.', 'Establishment', ''].map((h, i) => (
                <th key={i} className="text-left px-2 py-[7px] text-[10px] font-bold uppercase tracking-[.7px] text-[#7a7060]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {coverage.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-center text-[11.5px] text-[#7a7060]">
                  No registrations recorded
                </td>
              </tr>
            ) : coverage.map((c, i) => (
              <tr key={i} style={{ borderBottom: i < coverage.length - 1 ? '1px solid #e8e3d8' : 'none', background: i % 2 === 0 ? '#fff' : '#faf9f6' }}>
                <td className={cellCls}>
                  <Select size="sm" value={c.statute} onChange={(v) => updateCov(i, 'statute', v)} options={STATUTES} />
                </td>
                <td className={cellCls}>
                  <DateField size="sm" value={toIso(c.coveredFrom)} onChange={(v) => updateCov(i, 'coveredFrom', v)} placeholder="From" />
                </td>
                <td className={cellCls}>
                  <DateField size="sm" value={toIso(c.coveredTo)} onChange={(v) => updateCov(i, 'coveredTo', v)} placeholder="Still running" />
                </td>
                <td className={cellCls}>
                  <input value={c.registrationNo || ''} onChange={(e) => updateCov(i, 'registrationNo', e.target.value)} className={inputSmClass} placeholder="e.g. MP/12345" />
                </td>
                <td className={cellCls}>
                  <Select size="sm" value={c.establishmentRef || ''} onChange={(v) => updateCov(i, 'establishmentRef', v)} options={establishmentOptions} />
                </td>
                <td className={cellCls}>
                  <RemoveRowButton onClick={() => removeCov(i)} title="Remove registration" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionShell>
  );

  // ── 4. Establishments ─────────────────────────────────────────────────────
  const updateEst = (i, field, val) => setField('establishments', establishments.map((e, idx) => (idx === i ? { ...e, [field]: val } : e)));
  const removeEst = (i) => setField('establishments', establishments.filter((_, idx) => idx !== i));
  const addEst = () => setField('establishments', [...establishments, {
    label: '', state: '', city: '', type: 'branch', employeeCount: '', esiEligibleCount: '',
    commencedOn: '', closedOn: '', isFactory: false, pollutionCategory: '', pollutionCategorySource: 'declared',
  }]);

  const establishmentsSection = (
    <SectionShell
      anchorKey="establishments"
      title="Establishments"
      question="Where does it operate?"
      count={establishments.length}
      open={openSections.establishments}
      onToggle={() => toggleSection('establishments')}
      action={<AddRowButton onClick={addEst}>Add establishment</AddRowButton>}
    >
      <div className={`${noteCls} mb-2.5`}>
        One row per premises. State decides professional tax, shops &amp; establishments,
        trade licence and labour welfare fund — all state subjects, all different.
        Fifteen staff in Gujarat and ten in Maharashtra is not twenty-five in one place,
        and neither site crosses the EPF threshold.
      </div>

      {establishments.length === 0 ? (
        <div className="rounded-xl border px-3 py-5 text-center text-[11.5px] text-[#7a7060]" style={{ borderColor: '#ddd6c6' }}>
          No establishments recorded — state-linked compliances cannot be determined
        </div>
      ) : (
        <div className="space-y-2">
          {establishments.map((e, i) => (
            <div key={i} className="rounded-xl border p-2.5" style={{ borderColor: '#ddd6c6', background: i % 2 === 0 ? '#fff' : '#faf9f6' }}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-bold uppercase tracking-[.7px] text-[#7a7060]">
                  Establishment {i + 1}
                </span>
                <div className="ml-auto">
                  <RemoveRowButton onClick={() => removeEst(i)} title="Remove establishment" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-2">
                <div>
                  <div className={microLabel}>Label</div>
                  <input value={e.label || ''} onChange={(ev) => updateEst(i, 'label', ev.target.value)} className={inputSmClass} placeholder="e.g. Head office" />
                </div>
                <div>
                  <div className={microLabel}>State</div>
                  <Select size="sm" value={e.state || ''} onChange={(v) => updateEst(i, 'state', v)} options={stateOptions} />
                  {!String(e.state || '').trim() && (
                    <DropWarning>Pick a state or this whole establishment is not saved</DropWarning>
                  )}
                </div>
                <div>
                  <div className={microLabel}>City</div>
                  <input value={e.city || ''} onChange={(ev) => updateEst(i, 'city', ev.target.value)} className={inputSmClass} placeholder="e.g. Ahmedabad" />
                </div>
                <div>
                  <div className={microLabel}>Type</div>
                  <Select size="sm" value={e.type || 'branch'} onChange={(v) => updateEst(i, 'type', v)} options={ESTABLISHMENT_TYPES} />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-2">
                <div>
                  <div className={microLabel}>Employees here</div>
                  <input value={e.employeeCount ?? ''} onChange={(ev) => updateEst(i, 'employeeCount', ev.target.value)} className={inputSmClass} placeholder="e.g. 18" />
                </div>
                <div>
                  <div className={microLabel}>ESI-eligible here</div>
                  <input value={e.esiEligibleCount ?? ''} onChange={(ev) => updateEst(i, 'esiEligibleCount', ev.target.value)} className={inputSmClass} placeholder="e.g. 12" />
                </div>
                <div>
                  <div className={microLabel}>Commenced on</div>
                  <DateField size="sm" value={toIso(e.commencedOn)} onChange={(v) => updateEst(i, 'commencedOn', v)} placeholder="Start date" />
                </div>
                <div>
                  <div className={microLabel}>Closed on</div>
                  <DateField size="sm" value={toIso(e.closedOn)} onChange={(v) => updateEst(i, 'closedOn', v)} placeholder="Still open" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
                <div className="flex items-center gap-2 pb-1.5">
                  <input
                    type="checkbox"
                    checked={Boolean(e.isFactory)}
                    onChange={(ev) => updateEst(i, 'isFactory', ev.target.checked)}
                    className="w-4 h-4 accent-[#145f5f] cursor-pointer"
                    id={`est-factory-${i}`}
                  />
                  <label htmlFor={`est-factory-${i}`} className="text-[11.5px] text-[#3a352c] cursor-pointer">
                    Factory under the Factories Act
                  </label>
                </div>
                <div>
                  <div className={microLabel}>Pollution category</div>
                  <Select size="sm" value={e.pollutionCategory || ''} onChange={(v) => updateEst(i, 'pollutionCategory', v)} options={POLLUTION_CATEGORIES} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionShell>
  );

  // ── 5. Business & operations ──────────────────────────────────────────────
  const operations = (
    <SectionShell
      anchorKey="operations"
      title="Business &amp; operations"
      question="What does it do?"
      open={openSections.operations}
      onToggle={() => toggleSection('operations')}
    >
      <div className={`${noteCls} mb-2.5`}>
        Decides sector-specific licensing, the DPDP Act, CERT-In reporting and the FEMA / RBI filings.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <div>
          <div className={microLabel}>NIC code</div>
          <input value={form.nicCode || ''} onChange={(e) => setField('nicCode', e.target.value)} className={inputSmClass} placeholder="e.g. 15400" style={{ fontFamily: "'JetBrains Mono', monospace" }} />
        </div>
        <div className="md:col-span-2">
          <div className={microLabel}>Sector / principal business activity</div>
          <input value={form.sector || ''} onChange={(e) => setField('sector', e.target.value)} className={inputSmClass} placeholder="e.g. Manufacture of food products" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
        <TriField label="Digital operations" value={form.hasDigitalOperations} onChange={(v) => setField('hasDigitalOperations', v)} note="Website, app or online service" />
        <TriField label="Processes personal data" value={form.processesPersonalData} onChange={(v) => setField('processesPersonalData', v)} note="DPDP Act 2023" />
        <TriField label="External commercial borrowing" value={form.hasEcb} onChange={(v) => setField('hasEcb', v)} note="ECB-2 monthly return" />
        <TriField label="Overseas investment" value={form.hasOverseasInvestment} onChange={(v) => setField('hasOverseasInvestment', v)} note="Annual Performance Report" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <div className={microLabel}>Foreign shareholding (%)</div>
          <input value={form.foreignShareholdingPercent ?? ''} onChange={(e) => setField('foreignShareholdingPercent', e.target.value)} className={inputSmClass} placeholder="e.g. 26" />
          <div className="mt-[3px] text-[10px] text-[#7a7060]">Drives FLA return and FC-GPR</div>
        </div>
      </div>
    </SectionShell>
  );

  // ── Group structure, securities & tax ─────────────────────────────────────
  const updateGst = (i, field, val) => setField('gstRegistrations', gstRegs.map((g, idx) => (idx === i ? { ...g, [field]: val } : g)));
  const removeGst = (i) => setField('gstRegistrations', gstRegs.filter((_, idx) => idx !== i));
  const addGst = () => setField('gstRegistrations', [...gstRegs, { state: '', gstin: '', registeredOn: '', cancelledOn: '' }]);

  const toggleExchange = (ex) => setField('exchanges', exchanges.includes(ex) ? exchanges.filter((x) => x !== ex) : [...exchanges, ex]);

  const structure = (
    <SectionShell
      anchorKey="structure"
      title="Group, securities &amp; tax"
      question="Supporting facts"
      count={gstRegs.length}
      open={openSections.structure}
      onToggle={() => toggleSection('structure')}
    >
      <div className={`${noteCls} mb-2.5`}>
        Facts the company type alone does not settle — consolidation, the small-company
        carve-outs, SEBI LODR and the tax registrations.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
        <TriField label="Holding company" value={form.isHoldingCompany} onChange={(v) => setField('isHoldingCompany', v)} note="Triggers consolidation" />
        <TriField label="Subsidiary company" value={form.isSubsidiaryCompany} onChange={(v) => setField('isSubsidiaryCompany', v)} note="Blocks small-company status" />
        <TriField label="Government company" value={form.isGovernmentCompany} onChange={(v) => setField('isGovernmentCompany', v)} note="CAG audit" />
        <TriField label="Governed by a special Act" value={form.governedBySpecialAct} onChange={(v) => setField('governedBySpecialAct', v)} note="Banking, insurance, electricity" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
        <TriField label="Chairman is executive" value={form.chairmanIsExecutive} onChange={(v) => setField('chairmanIsExecutive', v)} note="Board composition under LODR" />
        <TriField label="Securities in demat" value={form.securitiesInDemat} onChange={(v) => setField('securitiesInDemat', v)} note="Rule 9A dematerialisation" />
        <div className="md:col-span-2">
          <div className={microLabel}>Listed on</div>
          <div className="flex flex-wrap gap-2 pt-1">
            {EXCHANGES.map((ex) => (
              <label key={ex} className="inline-flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={exchanges.includes(ex)}
                  onChange={() => toggleExchange(ex)}
                  className="w-4 h-4 accent-[#145f5f] cursor-pointer"
                />
                <span className="text-[11.5px] text-[#3a352c]">{ex}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
        <div>
          <div className={microLabel}>PAN</div>
          <input
            value={form.pan || ''}
            onChange={(e) => setField('pan', e.target.value.toUpperCase())}
            className={inputSmClass}
            placeholder="AAACX1234X"
            maxLength={10}
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
          />
        </div>
        <TriField label="International transactions" value={form.hasInternationalTransactions} onChange={(v) => setField('hasInternationalTransactions', v)} note="Form 3CEB transfer pricing" />
        <TriField label="Specified domestic transactions" value={form.hasSpecifiedDomesticTransactions} onChange={(v) => setField('hasSpecifiedDomesticTransactions', v)} note="Form 3CEB" />
        <TriField label="Deducts TDS" value={form.isTdsDeductor} onChange={(v) => setField('isTdsDeductor', v)} note="Quarterly 24Q / 26Q" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
        <TriField label="DPIIT-recognised startup" value={form.isDpiitStartup} onChange={(v) => setField('isDpiitStartup', v)} note="Exemptions and carve-outs" />
        <div className="md:col-span-2">
          <div className={microLabel}>DPIIT recognition no.</div>
          <input value={form.dpiitRecognitionNo || ''} onChange={(e) => setField('dpiitRecognitionNo', e.target.value)} className={inputSmClass} placeholder="e.g. DIPP12345" />
        </div>
      </div>

      <div className="flex items-center gap-2 mb-1">
        <div className={microLabel} style={{ marginBottom: 0 }}>GST registrations</div>
        <AddRowButton onClick={addGst}>Add GSTIN</AddRowButton>
      </div>
      <div className="mb-1.5 text-[10px] text-[#7a7060]">
        One per state. GST returns are filed per registration, not per company.
      </div>
      <div className="rounded-xl overflow-hidden border" style={{ borderColor: '#ddd6c6' }}>
        <table className="w-full border-collapse" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: 'auto' }} />
            <col style={{ width: '180px' }} />
            <col style={{ width: '140px' }} />
            <col style={{ width: '140px' }} />
            <col style={{ width: '30px' }} />
          </colgroup>
          <thead>
            <tr style={{ background: '#f8f5ef', borderBottom: '1px solid #ddd6c6' }}>
              {['State', 'GSTIN', 'Registered on', 'Cancelled on', ''].map((h, i) => (
                <th key={i} className="text-left px-2 py-[7px] text-[10px] font-bold uppercase tracking-[.7px] text-[#7a7060]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {gstRegs.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-4 text-center text-[11.5px] text-[#7a7060]">
                  No GST registrations recorded
                </td>
              </tr>
            ) : gstRegs.map((g, i) => (
              <tr key={i} style={{ borderBottom: i < gstRegs.length - 1 ? '1px solid #e8e3d8' : 'none', background: i % 2 === 0 ? '#fff' : '#faf9f6' }}>
                <td className={cellCls}>
                  <Select size="sm" value={g.state || ''} onChange={(v) => updateGst(i, 'state', v)} options={stateOptions} />
                </td>
                <td className={cellCls}>
                  <input
                    value={g.gstin || ''}
                    onChange={(e) => updateGst(i, 'gstin', e.target.value.toUpperCase())}
                    className={inputSmClass}
                    placeholder="24AAACX1234X1ZP"
                    maxLength={20}
                    style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '10.5px' }}
                  />
                </td>
                <td className={cellCls}>
                  <DateField size="sm" value={toIso(g.registeredOn)} onChange={(v) => updateGst(i, 'registeredOn', v)} placeholder="From" />
                </td>
                <td className={cellCls}>
                  <DateField size="sm" value={toIso(g.cancelledOn)} onChange={(v) => updateGst(i, 'cancelledOn', v)} placeholder="Active" />
                </td>
                <td className={cellCls}>
                  <RemoveRowButton onClick={() => removeGst(i)} title="Remove GSTIN" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionShell>
  );

  // "Fill" opens the section rather than toggling it — a user who clicks Fill
  // on an already-open section wants to see it, not close it — then scrolls to
  // it. Without the scroll the click appears to do nothing: the section that
  // opened is several hundred pixels below the fold.
  const openSection = (key) => {
    if (!openSections[key]) toggleSection(key);
    // Next frame, so the section has rendered before we scroll to it.
    requestAnimationFrame(() => {
      document.getElementById(sectionAnchorId(key))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  return (
    <>
      <ProfileLedger form={form} setField={setField} openSection={openSection} />
      {financials}
      {workforce}
      {establishmentsSection}
      {operations}
      {structure}
    </>
  );
}
